import { useTranslation } from "react-i18next";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import { CustomerDisplayFieldSelect } from "./CustomerDisplayFieldSelect";
import { FormSection } from "../../../components/FormSection";
import { getEffectiveBookkeepingMapping } from "@KassaroAB/shared/utils/bookkeepingDefaultAccounts";
import type { PaymentMethodInvoice } from "@KassaroAB/shared/types/paymentMethods/paymentMethod";
import { FieldGroupPaymentMethodFields } from "../paymentMethodFieldGroup";
import { usePaymentMethodForm } from "../paymentMethodFormHook";
import {
  PaymentMethodFormContainer,
  type PaymentMethodFormProps,
} from "./PaymentMethodFormContainer";
import { basePaymentMethodValues } from "./basePaymentMethodValues";
import { Fields, IntegrationField, TextField, ToggleField } from "./paymentMethodFields";

export const InvoiceForm = ({
  formId,
  store,
  paymentMethod,
  savedTick,
  onSubmit,
  onDirtyChange,
}: PaymentMethodFormProps<Omit<PaymentMethodInvoice, "id">>) => {
  const { t } = useTranslation();
  const mergedAccounts = getEffectiveBookkeepingMapping(
    store.country,
    store.bookkeepingDefaultAccounts,
  );
  const form = usePaymentMethodForm({
    defaultValues: {
      ...basePaymentMethodValues(paymentMethod),
      integrationId: paymentMethod.integrationId ?? "",
      paymentDuration: paymentMethod.paymentDuration?.toString() ?? "",
      documentHeaderText: paymentMethod.documentHeaderText ?? "",
      customerSecondaryField: paymentMethod.customerSecondaryField,
      customerThirdField: paymentMethod.customerThirdField,
      printSignatureReceipt: paymentMethod.printSignatureReceipt ?? false,
      emailDocumentToStore: paymentMethod.emailDocumentToStore ?? false,
    },
    onSubmit: ({ value }) =>
      onSubmit({
        ...value,
        integrationId: value.integrationId || null,
        paymentDuration: value.paymentDuration.trim()
          ? parseInt(value.paymentDuration, 10)
          : undefined,
      }),
  });

  return (
    <PaymentMethodFormContainer
      formId={formId}
      form={form}
      savedTick={savedTick}
      onDirtyChange={onDirtyChange}
    >
      <FieldGroupPaymentMethodFields
        form={form}
        fields={{
          name: "name",
          type: "type",
          bookkeepingAccountId: "bookkeepingAccountId",
          color: "color",
        }}
        defaultAccountNumber={mergedAccounts.businessAccount}
        t={t}
      />
      <FormSection title={t("paymentMethodForm.additionalSettings")}>
        <Fields>
          <form.Field name="integrationId">
            {(field) => (
              <IntegrationField
                label={t("paymentMethodForm.integration")}
                placeholder={t("paymentMethodForm.noIntegration")}
                chainId={store.chainId}
                integrationTypes={[IntegrationType.FORTNOX, IntegrationType.SPIRIS, IntegrationType.BJORN_LUNDEN]}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                errors={field.state.meta.errors}
              />
            )}
          </form.Field>
          <form.Field
            name="paymentDuration"
            validators={{
              onChange: ({ value }) => {
                const trimmed = value.trim();
                if (!trimmed) {
                  return paymentMethod.paymentDuration === undefined
                    ? undefined
                    : t("paymentMethodForm.invoice.paymentDurationRequired");
                }
                return /^[1-9]\d*$/.test(trimmed) &&
                  Number.isSafeInteger(parseInt(trimmed, 10))
                  ? undefined
                  : t("paymentMethodForm.invoice.paymentDurationInvalid");
              },
            }}
          >
            {(field) => (
              <TextField
                label={t("paymentMethodForm.invoice.paymentDuration")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                errors={field.state.meta.errors}
                placeholder={t("paymentMethodForm.invoice.paymentDurationPlaceholder")}
                inputMode="numeric"
              />
            )}
          </form.Field>
          <form.Field name="documentHeaderText">
            {(field) => (
              <TextField
                label={t("paymentMethodForm.invoice.documentHeaderText")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                placeholder={t("paymentMethodForm.documentHeaderTextPlaceholder")}
              />
            )}
          </form.Field>
          <form.Field name="customerSecondaryField">
            {(field) => (
              <CustomerDisplayFieldSelect
                label={t("paymentMethodForm.customerSecondaryField")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="customerThirdField">
            {(field) => (
              <CustomerDisplayFieldSelect
                label={t("paymentMethodForm.customerThirdField")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="emailDocumentToStore">
            {(field) => (
              <ToggleField
                label={t("paymentMethodForm.emailDocumentToStore")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="printSignatureReceipt">
            {(field) => (
              <ToggleField
                label={t("paymentMethodForm.printSignatureReceipt")}
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </PaymentMethodFormContainer>
  );
};
